// DOM-слой (Preact + сигналы, ADR-0001): рендер по состоянию навигации. Системный экран перекрывает всё;
// дальше — служебный стек, затем текущий экран, лист окна, подтверждение ⛔, тост, OVL-ROTATE.
// Раскладка ux/12 §1: на XL+ нижняя навигация уходит в левую колонку.
import { DebugPanel } from './debug-panel.js';
import { signal } from '@preact/signals';
import type { ComponentType } from 'preact';
import { useRef } from 'preact/hooks';
import { useApp } from '../app/app.js';
import { SCREENS as LEVELS, type ModalId, type ScreenId, type SheetEntry, type SystemId } from '../nav/navigator.js';
import { useScreenTransition, useSheetTransition } from './transitions.js';
import { viewport } from '../telegram/viewport.js';
import type { Stage } from '../scene/stage.js';
import { ActionBar, NavBar, Strips, ToastView } from './kit.js';
import { SYSTEM_SCREENS } from '../screens/system.js';
import {
  CoopBuildScreen, CoopCreateScreen, CoopDonateSheet, CoopLeaveSheet, CoopMembersScreen, CoopPreviewSheet, CoopRequestsScreen,
  CoopSearchScreen, CoopSettingsScreen, CoopShopSheet, CoopStorageScreen, CoopTabScreen, DisbandVoteSheet, MemberSheet,
} from '../screens/coop.js';
import { ChatMessageSheet, CoopChatScreen, TgGroupSheet } from '../screens/chat.js';
import { QuestsScreen } from '../screens/quests.js';
import { MailLetterSheet, MailScreen } from '../screens/mail.js';
import { HomeScreen } from '../screens/home.js';
import { MapScreen, BossSheet, BossLockedSheet } from '../screens/map.js';
import { FightScreen, FightResultScreen, FightLeaveDialog, ContributionSheet } from '../screens/fight.js';
import { CharScreen, StatsSheet, LevelUpSheet } from '../screens/char.js';
import { InventoryScreen, ItemSheet, UpgradeSheet, DismantleDialog } from '../screens/inventory.js';
import { StoreScreen, PurchaseSheet, PayPendingDialog, PaySuccessSheet, PayErrorSheet } from '../screens/store.js';
import { ResourceInfoSheet, EnergySheet, RewardSheet, ConfirmDialog, SessionElsewhereDialog, ConsentDialog, TutDoneDialog } from '../screens/modals.js';
import { AccountScreen, LegalScreen, SettingsScreen, SupportScreen } from '../screens/settings.js';
import { ZoneSheet, ZoneUpgradedSheet } from '../screens/garage.js';
import { CarScreen, CarNodeSheet, TripsScreen, TripStartSheet, TripResultSheet } from '../screens/car.js';
import { GarageVisitScreen, PlayerCardSheet } from '../screens/visit.js';
import { ThermosSheet } from '../screens/store-thermos.js';
import { PurchasesScreen, PurchaseDetailsSheet, RefundNoticeSheet } from '../screens/store-purchases.js';
import { ShowcaseSheet } from '../screens/store-showcase.js';
import { BoxOpenSheet, OddsSheet } from '../screens/boxes.js';
import { SetsScreen, S8TokensSheet } from '../screens/sets.js';
import { TransferDialog } from '../screens/transfer.js';
import { BossCallSheet, InstanceResultSheet } from '../screens/instances.js';
import { FriendsScreen, InviteSheet, MoreSheet } from '../screens/friends.js';
import { TutorialOverlay } from '../tutorial/overlay.js';
import { I } from '../assets/icons.js';
import { NineSliceStyles, ScreenBg } from '../assets/ui.js';
import { t as tr } from '../i18n/t.js';

export const stage = signal<Stage | null>(null);
/** Для совместимости со старым экраном загрузки каркаса. */
export const stageReady = signal(false);

/** Фон экрана из манифеста (слой под содержимым); у сцен PixiJS — свой фон на холсте. */
// ID и назначение — поле `use` манифеста арта (docs/art-prod). SCR-HOME рисует BG-GARAGE на сцене PixiJS;
// BG-YARD — двор кооператива (SCR-COOP, этап А). Вариант -XL берётся на ширине ≥ 1024 (bgId).
export const SCREEN_BG: Partial<Record<ScreenId, string>> = {
  'SCR-MAP': 'BG-MAP-D1', 'SCR-STORE': 'BG-SHOP', 'SCR-CHAR': 'BG-CHAR', 'SCR-INVENTORY': 'BG-CHAR',
  'SCR-FIGHT-RESULT': 'BG-RESULT',
  'SCR-GARAGE-VISIT': 'BG-GARAGE',
  'SCR-COOP': 'BG-YARD',
  'SCR-PURCHASES': 'BG-SHOP',
  'SCR-SETS': 'BG-CHAR',
  'SCR-FRIENDS': 'BG-GARAGE',
};

export interface ScreenProps { params: Record<string, unknown>; entryKey: number }
export interface SheetProps { entry: SheetEntry }

const SCREENS: Record<ScreenId, ComponentType<ScreenProps>> = {
  'SCR-HOME': HomeScreen,
  'SCR-MAP': MapScreen,
  'SCR-STORE': StoreScreen,
  'SCR-CHAR': CharScreen,
  'SCR-INVENTORY': InventoryScreen,
  'SCR-FIGHT': FightScreen,
  'SCR-FIGHT-RESULT': FightResultScreen,
  'SCR-SETTINGS': SettingsScreen,
  'SCR-ACCOUNT': AccountScreen,
  'SCR-SUPPORT': SupportScreen,
  'SCR-LEGAL': LegalScreen,
  'SCR-CAR': CarScreen,
  'SCR-TRIPS': TripsScreen,
  'SCR-GARAGE-VISIT': GarageVisitScreen,
  'SCR-COOP': CoopTabScreen,
  'SCR-COOP-SEARCH': CoopSearchScreen,
  'SCR-COOP-CREATE': CoopCreateScreen,
  'SCR-COOP-MEMBERS': CoopMembersScreen,
  'SCR-COOP-REQUESTS': CoopRequestsScreen,
  'SCR-COOP-SETTINGS': CoopSettingsScreen,
  'SCR-COOP-BUILD': CoopBuildScreen,
  'SCR-COOP-STORAGE': CoopStorageScreen,
  'SCR-COOP-CHAT': CoopChatScreen,
  'SCR-PURCHASES': PurchasesScreen,
  'SCR-SETS': SetsScreen,
  'SCR-QUESTS': QuestsScreen,
  'SCR-MAIL': MailScreen,
  'SCR-FRIENDS': FriendsScreen,
};

const SHEETS: Record<ModalId, ComponentType<SheetProps>> = {
  'MOD-BOSS': BossSheet,
  'MOD-BOSS-LOCKED': BossLockedSheet,
  'MOD-ENERGY': EnergySheet,
  'MOD-RESOURCE-INFO': ResourceInfoSheet,
  'MOD-REWARD': RewardSheet,
  'MOD-STATS': StatsSheet,
  'MOD-LEVEL-UP': LevelUpSheet,
  'MOD-ITEM': ItemSheet,
  'MOD-UPGRADE': UpgradeSheet,
  'MOD-DISMANTLE': DismantleDialog,
  'MOD-PURCHASE': PurchaseSheet,
  'MOD-PAY-PENDING': PayPendingDialog,
  'MOD-PAY-SUCCESS': PaySuccessSheet,
  'MOD-PAY-ERROR': PayErrorSheet,
  'MOD-CONTRIBUTION': ContributionSheet,
  'MOD-FIGHT-LEAVE': FightLeaveDialog,
  'MOD-CONFIRM': ConfirmDialog,
  'MOD-TUT-DONE': TutDoneDialog,
  'MOD-SESSION-ELSEWHERE': SessionElsewhereDialog,
  'MOD-CONSENT': ConsentDialog,
  'MOD-ZONE': ZoneSheet,
  'MOD-ZONE-UPGRADED': ZoneUpgradedSheet,
  'MOD-CAR-NODE': CarNodeSheet,
  'MOD-TRIP-START': TripStartSheet,
  'MOD-TRIP-RESULT': TripResultSheet,
  'MOD-PLAYER-CARD': PlayerCardSheet,
  'MOD-COOP-PREVIEW': CoopPreviewSheet,
  'MOD-MEMBER': MemberSheet,
  'MOD-COOP-LEAVE': CoopLeaveSheet,
  'MOD-COOP-DISBAND-VOTE': DisbandVoteSheet,
  'MOD-COOP-DONATE': CoopDonateSheet,
  'MOD-COOP-SHOP': CoopShopSheet,
  'MOD-CHAT-MESSAGE': ChatMessageSheet,
  'MOD-TG-GROUP': TgGroupSheet,
  'MOD-THERMOS': ThermosSheet,
  'MOD-REFUND-NOTICE': RefundNoticeSheet,
  'MOD-PURCHASE-DETAILS': PurchaseDetailsSheet,
  'MOD-SHOWCASE': ShowcaseSheet,
  'MOD-BOX-OPEN': BoxOpenSheet,
  'MOD-ODDS': OddsSheet,
  'MOD-S8-TOKENS': S8TokensSheet,
  'MOD-TRANSFER': TransferDialog,
  'MOD-COOP-BOSS-CALL': BossCallSheet,
  'MOD-INSTANCE-RESULT': InstanceResultSheet,
  'MOD-MAIL-LETTER': MailLetterSheet,
  'MOD-INVITE': InviteSheet,
  'MOD-MORE': MoreSheet,
};

function SheetHost(p: { entry: SheetEntry }) {
  const app = useApp();
  const C = (SHEETS as Record<string, ComponentType<SheetProps>>)[p.entry.id];
  if (C) return <C key={p.entry.key} entry={p.entry} />;
  // Гостевой экран полноразмерным листом (ux/02 §2.1 п. 2).
  const S = SCREENS[p.entry.id as ScreenId];
  return (
    <div class="overlay" data-testid="guest-sheet">
      <section class="sheet full" role="dialog" aria-modal="true" style={{ position: 'relative' }}>
        <button class="guest-close" data-testid="guest-close" aria-label={tr('UI-BTN-CLOSE')} onClick={() => app.nav.popSheet()}><I n="close" size={18} /></button>
        <div class="shell" style={{ position: 'relative', flex: 1 }}><S key={p.entry.key} params={{ ...p.entry.params, guest: true }} entryKey={p.entry.key} /></div>
      </section>
    </div>
  );
}

export function App() {
  const app = useApp();
  void app.navVersion.value;
  const vp = viewport.value;
  const s = app.nav.state;
  const sys = s.system;
  const Sys = sys ? SYSTEM_SCREENS[sys.id as SystemId] : null;
  const service = s.service.at(-1);
  const top = app.nav.topEntry();
  const screenId = app.nav.top();
  const Screen = SCREENS[screenId];
  const navVisible = app.nav.navVisible();
  const side = navVisible && (vp.width === 'XL' || vp.width === 'XXL');
  const sheet = s.sheets.at(-1);
  // Переходы по уровням ux/02: экран — внутри .shell, листы и окна — дети #ui.
  const shellRef = useRef<HTMLDivElement>(null);
  const uiRef = useRef<HTMLElement | null>(globalThis.document?.getElementById('ui') ?? null);
  useScreenTransition(shellRef, { key: `${s.tab}:${top?.key ?? 0}`, level: LEVELS[screenId].level, depth: s.stack.length, tab: s.tab });
  useSheetTransition(uiRef, `${sys ? 'sys' : ''}|${sheet?.key ?? ''}|${s.confirm?.key ?? ''}`);
  return (
    <>
      {!sys || sys.id === 'SCR-TUT-LETTER' ? (
        <div class="layout" data-screen={screenId}>
          {side ? <NavBar side /> : null}
          <div class="main-col">
            {SCREEN_BG[screenId] ? <ScreenBg id={SCREEN_BG[screenId]!} /> : null}
            <div class="shell" ref={shellRef}>
              <Screen key={top?.key ?? screenId} params={top?.params ?? {}} entryKey={top?.key ?? 0} />
              <ActionBar last={!navVisible || side} />
              {navVisible && !side ? <NavBar /> : null}
            </div>
          </div>
        </div>
      ) : null}
      {service && !sys ? (
        <div class="layout" style={{ background: 'var(--bg)', zIndex: 5 }}>
          <div class="main-col"><div class="shell">{(() => { const S = SCREENS[service.id as ScreenId] ?? SettingsScreen; return <S key={service.key} params={service.params} entryKey={service.key} />; })()}</div></div>
        </div>
      ) : null}
      {!sys ? <div style={{ position: 'absolute', top: 'calc(var(--header-h) + var(--safe-top))', left: 0, right: 0, zIndex: 8 }}><Strips /></div> : null}
      {sheet && !sys ? <SheetHost entry={sheet} /> : null}
      {s.confirm && !sys ? <SheetHost entry={s.confirm} /> : null}
      {!sys ? <TutorialOverlay /> : null}
      {Sys ? <Sys params={sys!.params} /> : null}
      <ToastView />
      <DebugPanel />
      <NineSliceStyles />
      {vp.rotate ? <div class="rotate" data-testid="OVL-ROTATE"><I n="phone" size={56} class="ill" />{tr('CL-UI-APP-01')}</div> : null}
    </>
  );
}
